import { useState, useCallback, useMemo } from 'react';
import PLTable from './PLTable';
import DivisionComparison from './DivisionComparison';
import SectionLabel from '../common/SectionLabel';
import { useFinancialData, ACTUALS_CUTOFF_2026 } from '../../context/FinancialDataContext';
import { usePipeline } from '../../context/PipelineContext';

const ALL_YEARS = ['2027', '2026', '2025', '2024', '2023', '2022', '2021', '2020', '2019'];
const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const CLASSES = [
  { key: 'global', label: 'Global' },
  { key: 'bwm', label: 'BWM' },
  { key: 'bts', label: 'BTS' },
  { key: 'bia', label: 'BIA' },
];
const CLASS_START_YEAR = 2024;

function sumCat(monthData, category) {
  if (!monthData?.[category]) return 0;
  return Object.values(monthData[category]).reduce((s, v) => s + (v || 0), 0);
}

export default function PLView({ scenarioImpact }) {
  const { editableByClass, updateCell, resetProjections, saveAsDefault, allPLData, forecastScenario, kpi, forecast2027, forecastExpenseGrowth, setForecastExpenseGrowth } = useFinancialData();
  const { baselineForecastRevenue } = usePipeline();
  const [saveConfirm, setSaveConfirm] = useState(false);
  const [showForecast, setShowForecast] = useState(() => {
    try { const v = localStorage.getItem('bcc_pl_show_forecast'); return v !== null ? v === 'true' : true; } catch { return true; }
  });
  const [showScenarios, setShowScenarios] = useState(false);

  const [activeYear, setActiveYear] = useState('2026');
  const [activeClass, setActiveClass] = useState('global');

  const classesAvailable = parseInt(activeYear) >= CLASS_START_YEAR;

  const handleYearChange = useCallback((year) => {
    setActiveYear(year);
    if (parseInt(year) < CLASS_START_YEAR) {
      setActiveClass('global');
    }
  }, []);

  // Get the data for the current selection
  const currentData = useMemo(() => {
    if (activeYear === '2026') {
      return editableByClass[activeClass] || null;
    }
    if (activeYear === '2027') {
      return forecast2027?.[activeClass] || null;
    }
    return allPLData[activeClass]?.[activeYear] || null;
  }, [activeYear, activeClass, editableByClass, allPLData, forecast2027]);

  const isEditable = activeYear === '2026';
  const actualsCutoff = activeYear === '2026' ? ACTUALS_CUTOFF_2026 : null;

  // Handle inline cell edits - delegates to context (persists to localStorage)
  const handleCellChange = useCallback((month, category, key, value) => {
    updateCell(activeClass, month, category, key, value);
  }, [activeClass, updateCell]);

  // Prior year data for YoY comparison
  const priorYearData = useMemo(() => {
    // 2027's prior year is the live 2026 P&L (not in allPLData).
    if (activeYear === '2027') return editableByClass[activeClass] || null;
    const priorYear = String(parseInt(activeYear) - 1);
    return allPLData[activeClass]?.[priorYear] || null;
  }, [activeYear, activeClass, allPLData, editableByClass]);

  const yearLabel = activeYear === '2026' ? 'Budget' : activeYear === '2027' ? 'Forecast' : 'Actuals';
  const hasData = currentData && currentData.months;

  // Prior year data for division comparison cards (all classes including global)
  const divisionPriorYear = useMemo(() => {
    if (activeYear === '2027') return editableByClass;
    const priorYear = String(parseInt(activeYear) - 1);
    const result = { global: allPLData.global?.[priorYear] || null };
    for (const cls of ['bwm', 'bts', 'bia']) {
      result[cls] = allPLData[cls]?.[priorYear] || null;
    }
    return result;
  }, [activeYear, allPLData, editableByClass]);

  // Division data for the selected year (for non-2026 years, pull from allPLData)
  const divisionDataForYear = useMemo(() => {
    if (activeYear === '2026') return editableByClass;
    if (activeYear === '2027') return forecast2027 || { global: null, bwm: null, bts: null, bia: null };
    const result = { global: allPLData.global?.[activeYear] || null };
    for (const cls of ['bwm', 'bts', 'bia']) {
      result[cls] = allPLData[cls]?.[activeYear] || null;
    }
    return result;
  }, [activeYear, editableByClass, allPLData, forecast2027]);

  // Growth info for 2026
  const growthInfo = useMemo(() => {
    if (activeYear !== '2026') return null;
    const data2025 = allPLData[activeClass]?.['2025'];
    const data2026 = allPLData[activeClass]?.['2026'];
    if (!data2025 || !data2026) return null;
    const actualMonths = MONTH_KEYS.slice(0, ACTUALS_CUTOFF_2026);
    const q1_2026 = actualMonths.reduce((s, m) => s + sumCat(data2026.months[m], 'income'), 0);
    const q1_2025 = actualMonths.reduce((s, m) => s + sumCat(data2025.months[m], 'income'), 0);
    if (!q1_2025) return null;
    return { factor: q1_2026 / q1_2025 };
  }, [activeYear, activeClass, allPLData]);

  // Show a banner explaining how the active scenario affects the forecast row
  const scenarioActive = forecastScenario && forecastScenario !== 'normal';

  return (
    <div>
      {scenarioActive && activeYear === '2026' && showForecast && (
        <div className={`mb-3 px-3 py-2 rounded-[4px] text-[11px] flex items-center gap-2 ${
          forecastScenario === 'conservative' ? 'bg-rose/10 text-rose' : 'bg-deep-teal/10 text-deep-teal'
        }`}>
          <span className="font-bold uppercase tracking-wider">{kpi.scenarioLabel} Scenario Active</span>
          <span className="opacity-80">
            P&L cells reflect committed revenue (unchanged). The Pipeline Forecast row below reflects scenario adjustments.
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <SectionLabel>Profit & Loss Statement</SectionLabel>
        <div className="flex items-center gap-3">
          {classesAvailable && (
            <div className="flex gap-0.5 bg-gray-100 rounded-[4px] p-0.5">
              {CLASSES.map((cls) => (
                <button
                  key={cls.key}
                  onClick={() => setActiveClass(cls.key)}
                  className={`px-3 py-1.5 text-[11px] font-bold rounded-[3px] transition-colors ${
                    activeClass === cls.key
                      ? 'bg-white text-navy shadow-sm'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {cls.label}
                </button>
              ))}
            </div>
          )}

          <select
            value={activeYear}
            onChange={(e) => handleYearChange(e.target.value)}
            className="px-3 py-1.5 text-[12px] font-bold text-navy bg-white border border-gray-300 rounded-[4px] focus:outline-none focus:border-bullogic-blue cursor-pointer"
          >
            {ALL_YEARS.map((year) => (
              <option key={year} value={year}>
                {year} {year === '2026' ? 'Budget' : year === '2027' ? 'Forecast' : 'Actuals'}
              </option>
            ))}
          </select>

          {activeYear === '2026' && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => { saveAsDefault(); setSaveConfirm(true); setTimeout(() => setSaveConfirm(false), 2000); }}
                className={`px-3 py-1.5 text-[11px] font-bold rounded-[4px] transition-colors ${
                  saveConfirm
                    ? 'bg-deep-teal text-white'
                    : 'text-deep-teal bg-deep-teal/10 hover:bg-deep-teal/20'
                }`}
                title="Save current values as the new default baseline"
              >
                {saveConfirm ? 'Saved!' : 'Save as Default'}
              </button>
              <button
                onClick={resetProjections}
                className="px-3 py-1.5 text-[11px] font-bold text-[#888] bg-gray-100 rounded-[4px] hover:bg-gray-200 transition-colors"
                title="Reset all 2026 projections to saved defaults"
              >
                Reset
              </button>
              <button
                onClick={() => { const next = !showForecast; setShowForecast(next); try { localStorage.setItem('bcc_pl_show_forecast', String(next)); } catch {} }}
                className={`px-3 py-1.5 text-[11px] font-bold rounded-[4px] transition-colors ${
                  showForecast
                    ? 'bg-bullogic-blue text-white'
                    : 'bg-gray-100 text-[#888] hover:bg-gray-200'
                }`}
                title="Show/hide pipeline forecast row in income section"
              >
                Forecast: {showForecast ? 'ON' : 'OFF'}
              </button>
              <button
                onClick={() => setShowScenarios(!showScenarios)}
                className={`px-3 py-1.5 text-[11px] font-bold rounded-[4px] transition-colors ${
                  showScenarios
                    ? 'bg-sky-blue text-white'
                    : 'bg-gray-100 text-[#888] hover:bg-gray-200'
                }`}
                title="Show/hide scenario impact row"
              >
                Scenarios: {showScenarios ? 'ON' : 'OFF'}
              </button>
            </div>
          )}

          {activeYear === '2027' && (
            <label className="flex items-center gap-1.5 text-[11px] font-bold text-navy">
              Expense growth
              <input
                type="number"
                value={Math.round((forecastExpenseGrowth ?? 0) * 100)}
                onChange={(e) => setForecastExpenseGrowth((parseFloat(e.target.value) || 0) / 100)}
                className="w-14 px-2 py-1 text-[12px] text-navy bg-white border border-gray-300 rounded-[4px] focus:outline-none focus:border-bullogic-blue"
                step="1"
              />
              %
            </label>
          )}
        </div>
      </div>

      {/* Info bar */}
      <div className="flex items-center gap-3 mb-3">
        <span className="text-[11px] font-light text-[#888]">
          {activeYear === '2026'
            ? 'Jan-Mar are closed actuals. Apr-Dec are projected from 2025 seasonal pattern. Click any value to edit. Edits save automatically.'
            : activeYear === '2027'
            ? 'Forecast: recurring + AUM continue and annualize, tax prep + commissions mirror 2026, tax planning starts fresh. Costs = 2026 grown by the rate at right. Read-only.'
            : `Read-only view of ${activeYear} ${yearLabel.toLowerCase()}${activeClass !== 'global' ? ` (${activeClass.toUpperCase()})` : ''}.`}
        </span>
        {growthInfo && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-[3px] bg-deep-teal/10 text-deep-teal">
            Q1 YoY: {growthInfo.factor >= 1 ? '+' : ''}{((growthInfo.factor - 1) * 100).toFixed(1)}%
          </span>
        )}
        {!classesAvailable && (
          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#aaa]">
            Class filtering available for 2024+
          </span>
        )}
      </div>

      {/* Division Comparison Cards (2024+ only) */}
      {classesAvailable && (
        <DivisionComparison
          editableByClass={divisionDataForYear}
          priorYearData={divisionPriorYear}
          activeClass={activeClass}
          onSelectClass={setActiveClass}
        />
      )}

      {/* Table */}
      {hasData ? (
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)]">
          <PLTable
            data={currentData}
            editable={isEditable}
            onCellChange={handleCellChange}
            actualsCutoff={actualsCutoff}
            priorYearData={priorYearData}
            forecastRevenue={showForecast && activeYear === '2026' ? (baselineForecastRevenue?.[activeClass] || null) : null}
            scenarioImpact={showScenarios && activeYear === '2026' ? scenarioImpact : null}
          />
        </div>
      ) : (
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-8 text-center">
          <p className="text-[13px] text-[#888]">
            No data available for {activeYear} ({CLASSES.find(c => c.key === activeClass)?.label}).
            {activeClass !== 'global' && parseInt(activeYear) < CLASS_START_YEAR && (
              <> Class-level data is only available from 2024 onward.</>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
